import React from 'react';
import { Upload, Button, message, Popconfirm } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { FuelReceipt } from '../../types';
import { fuelReceiptApi } from '../../services/api';

interface FuelReceiptImageProcessingUploadProps {
  t: (key: string) => string;
  isMobile: boolean;
  uploadKey: number;
  receiptImage: File | null;
  processing: boolean;
  processResult: any;
  uploadError: string;
  editingReceipt: FuelReceipt | null;
  onImageChange: (file: File | null) => void;
  onProcessResult: (result: any) => void;
  onUploadError: (error: string) => void;
  onEditingReceiptUpdate: (receipt: FuelReceipt) => void;
  onFetchReceipts: () => void;
  onProcessImage: () => void;
  validateFile: (file: File) => boolean;
}

const FuelReceiptImageProcessingUpload: React.FC<FuelReceiptImageProcessingUploadProps> = ({
  t,
  isMobile,
  uploadKey,
  receiptImage,
  processing,
  processResult,
  uploadError,
  editingReceipt,
  onImageChange,
  onProcessResult,
  onUploadError,
  onEditingReceiptUpdate,
  onFetchReceipts,
  onProcessImage,
  validateFile
}) => {
  const handleViewImage = () => {
    if (!editingReceipt) return;
    window.open(fuelReceiptApi.getReceiptImage(editingReceipt.id), '_blank');
  };

  const handleDownloadImage = () => {
    if (!editingReceipt) return;
    
    const link = document.createElement('a');
    link.href = fuelReceiptApi.downloadReceiptImage(editingReceipt.id);
    link.download = `fuel_receipt_${editingReceipt.id}.${editingReceipt.receiptImageContentType?.split('/')[1] || 'file'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteImage = async () => {
    if (!editingReceipt) return;
    
    try {
      await fuelReceiptApi.deleteReceiptImage(editingReceipt.id);
      message.success(t('file.deleted.successfully') || 'File deleted successfully');
      const updatedReceipt = await fuelReceiptApi.getById(editingReceipt.id);
      onEditingReceiptUpdate(updatedReceipt.data);
      onFetchReceipts();
    } catch (error: any) {
      message.error(error?.message || t('failed.to.delete.file') || 'Failed to delete file');
    }
  };

  return (
    <div style={{ marginBottom: 24, padding: isMobile ? 12 : 16, border: '1px solid #d9d9d9', borderRadius: 6, backgroundColor: '#fafafa' }}>
      <h4 style={{ fontSize: isMobile ? '14px' : '16px' }}>
        {t('receipt.image.processing') || 'Receipt Image Processing'}
      </h4>
      
      {editingReceipt && editingReceipt.hasReceiptImage && (
        <div style={{ marginBottom: 16 }}>
          <p><strong>{t('current.image') || 'Current Image'}:</strong></p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button 
              onClick={handleViewImage}
              icon={<span>👁️</span>}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? 'View' : t('view.current.image') || 'View Current Image'}
            </Button>
            <Button 
              onClick={handleDownloadImage}
              icon={<span>⬇️</span>}
              size={isMobile ? 'small' : 'middle'}
            >
              {t('download') || 'Download'}
            </Button>
            <Popconfirm
              title={t('delete.current.file.confirm') || 'Are you sure you want to delete the current file?'}
              onConfirm={handleDeleteImage}
              okText={t('yes')}
              cancelText={t('no')}
            >
              <Button 
                danger
                icon={<span>🗑️</span>}
                size={isMobile ? 'small' : 'middle'}
              >
                {t('delete.file') || 'Delete'}
              </Button>
            </Popconfirm>
          </div>
          <p style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
            {t('upload.new.to.replace') || 'Upload a new file below to replace the current image'}
          </p>
        </div>
      )}
      
      <Upload
        key={uploadKey}
        beforeUpload={(file) => {
          if (validateFile(file)) {
            onImageChange(file);
            onProcessResult(null);
            onUploadError('');
          }
          return false;
        }}
        showUploadList={true}
        accept="image/*,.pdf"
        maxCount={1}
        fileList={receiptImage ? [{
          uid: '-1',
          name: receiptImage.name,
          status: 'done' as const,
        }] : []}
        onRemove={() => {
          onImageChange(null);
          onProcessResult(null);
          onUploadError('');
        }}
      >
        <Button icon={<UploadOutlined />}>
          {t('upload.fuel.receipt.image') || 'Upload Fuel Receipt Image'}
        </Button>
      </Upload>
      
      <div style={{ marginTop: 12 }}>
        <Button
          type="primary"
          onClick={onProcessImage}
          disabled={!receiptImage || processing}
          loading={processing}
          block={isMobile}
        >
          {processing ? t('processing') + '...' : t('process.image') || 'Process Image'}
        </Button>
        {receiptImage && !isMobile && (
          <span style={{ marginLeft: 12, color: '#52c41a' }}>
            {t('file.ready') || 'File ready'}: {receiptImage.name}
          </span>
        )}
      </div>

      {uploadError && (
        <div style={{ color: 'red', marginTop: 8 }}>{uploadError}</div>
      )}
      {processResult && (
        <div style={{ marginTop: 12, background: '#f6f6f6', padding: 12, borderRadius: 6 }}>
          <b>{t('processed.data') || 'Processed Data'}:</b>
          <pre style={{ fontSize: '0.85em', maxHeight: 200, overflow: 'auto' }}>
            {JSON.stringify(processResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default FuelReceiptImageProcessingUpload;
